<script setup lang="ts">
import type { SavedView } from '@/modules/saved-views/types';
import { computed, nextTick, ref, shallowRef, watch } from 'vue';
import { savedViewsRegistry } from '@/modules/saved-views/store/registry';
import { UIConfigVisibility } from '@/connect/gen/consts/uiconfigconsts_pb';
import { pick } from 'lodash';
import { OrderbookState } from '@/modules/marketplace/services/orderbook-state-persistor';
import { useNoReactive } from '@/composables/reactivity';

const MAX_TITLE_LENGTH = 25;
const MAX_DESCRIPTION_LENGTH = 70;

const props = defineProps<{
  name: string;
  index: number;
  view: SavedView<OrderbookState>;
}>();

const name = useNoReactive(() => props.name, 'Saved views name should never change');

const savedViewsStore = savedViewsRegistry[name]();
const rTitle = shallowRef<HTMLInputElement | null>(null);
const rDescription = shallowRef<HTMLTextAreaElement | null>(null);

const tmpView = ref<Pick<SavedView<OrderbookState>, 'id' | 'title' | 'description' | 'visibility'>>(
  {
    title: '',
    description: '',
    visibility: UIConfigVisibility.PRIVATE,
  }
);

const isEditing = computed(() => savedViewsStore.editingView === props.view);

const activeField = ref<'title' | 'description'>('title');

const isTitleOverLimit = computed(() => tmpView.value.title.length > MAX_TITLE_LENGTH);

const isDescriptionOverLimit = computed(
  () => tmpView.value.description.length > MAX_DESCRIPTION_LENGTH
);

const isCharCountOverLimit = computed(() => {
  if (!isEditing.value) return false;
  return activeField.value === 'title' ? isTitleOverLimit.value : isDescriptionOverLimit.value;
});

const charCount = computed(() => {
  if (!isEditing.value) return '';
  return activeField.value === 'title'
    ? `${tmpView.value.title.length}/${MAX_TITLE_LENGTH}`
    : `${tmpView.value.description.length}/${MAX_DESCRIPTION_LENGTH}`;
});

const canSave = computed(() => {
  if (!isEditing.value) return false;
  if (tmpView.value.title.trim().length === 0) return false;
  if (isTitleOverLimit.value || isDescriptionOverLimit.value) return false;

  if (!props.view.id) {
    // we want to enable the save button right away when creating or duplicating
    return true;
  }

  return (
    tmpView.value.title !== props.view.title ||
    tmpView.value.description !== props.view.description ||
    tmpView.value.visibility !== props.view.visibility
  );
});

watch(
  isEditing,
  async (editing) => {
    if (editing) {
      initializeTmpView();
      await focusTitleInput();
    } else {
      // when exiting edit mode, resize textarea for view mode
      void handleInitialTextareaResize();
    }
  },
  { immediate: true }
);

function initializeTmpView() {
  tmpView.value = pick(props.view, ['id', 'title', 'description', 'visibility']);
  activeField.value = 'title';
}

async function focusTitleInput() {
  await nextTick();
  const titleInput = rTitle.value;
  if (!titleInput) return;

  titleInput.focus();

  if (tmpView.value.id) {
    // For existing views, place cursor at end
    titleInput.setSelectionRange(titleInput.value.length, titleInput.value.length);
  }
}

const visibilityOptions: Partial<
  Record<
    UIConfigVisibility,
    { icon: string; tooltip: { me: string; others?: string }; color: string }
  >
> = {
  [UIConfigVisibility.PRIVATE]: {
    icon: 'mdi-lock',
    tooltip: { me: 'Private: only I can see/edit' },
    color: 'amber',
  },
  [UIConfigVisibility.READONLY]: {
    icon: 'mdi-eye',
    tooltip: {
      me: 'Shared: I can see/edit and any colleagues can see',
      others: 'Shared:I can see but not edit',
    },
    color: 'blue',
  },
  [UIConfigVisibility.READWRITE]: {
    icon: 'mdi-account-group',
    tooltip: { me: 'Public: any colleague and I can see/edit' },
    color: 'green',
  },
};

function tooltipText(visibility: UIConfigVisibility) {
  return props.view.isOwner
    ? visibilityOptions[visibility]?.tooltip.me
    : visibilityOptions[visibility]?.tooltip.others || visibilityOptions[visibility]?.tooltip.me;
}

function handleEditClick() {
  savedViewsStore.setPreviousSelectedView(savedViewsStore.selectedView);
  savedViewsStore.setEditingView(props.view);
}

function handleSaveClick() {
  if (canSave.value) {
    savedViewsStore.saveChanges(
      tmpView.value.title,
      tmpView.value.description,
      tmpView.value.visibility
    );
  }
}

function handleKeyDown(ev: KeyboardEvent) {
  if (ev.key === 'Enter') {
    ev.preventDefault();
    if (canSave.value) {
      handleSaveClick();
    }
  } else if (ev.key === 'Escape') {
    ev.preventDefault();
    savedViewsStore.cancelEdit();
  }
}

function autoResizeTextarea(element: HTMLTextAreaElement) {
  // we first need to set to auto, otherwise the height will not auto reduce when we are removing text
  element.style.height = 'auto';
  element.style.height = `${element.scrollHeight}px`;
}

async function handleInitialTextareaResize() {
  await nextTick();
  const elDescription = rDescription.value;
  if (!elDescription) return;
  autoResizeTextarea(elDescription);
}

function handleTitleInput(ev: Event) {
  activeField.value = 'title';
  const element = ev.target as HTMLInputElement;
  tmpView.value.title = element.value;
}

function handleDescriptionInput(ev: Event) {
  activeField.value = 'description';
  const element = ev.target as HTMLTextAreaElement;
  tmpView.value.description = element.value;
  autoResizeTextarea(element);
}

function handleTitleFocus() {
  activeField.value = 'title';
}

function handleDescriptionFocus() {
  activeField.value = 'description';
}

function handleDragStart(ev: DragEvent) {
  if (isEditing.value) {
    ev.preventDefault();
  } else {
    savedViewsStore.handleDragStart(props.index);
  }
}

function handleDragOver(ev: DragEvent) {
  ev.preventDefault();
  if (ev.dataTransfer) {
    ev.dataTransfer.dropEffect = 'move';
  }
}

function handleDrop() {
  savedViewsStore.handleDrop(props.index);
}
</script>

<template>
  <VCard
    class="mb-2"
    :class="{
      'is-dragging': savedViewsStore.draggedIndex === index,
      'drag-over': savedViewsStore.draggedIndex !== null,
      selected: savedViewsStore.selectedIndex === index,
      'dirty-view': savedViewsStore.isStateDirty && savedViewsStore.selectedIndex === index,
    }"
    data-test="saved-view-card"
    draggable="true"
    outlined
    :ripple="false"
    @click="!isEditing ? savedViewsStore.selectView(view) : null"
    @dragend="savedViewsStore.handleDragEnd"
    @dragover="handleDragOver"
    @dragstart="handleDragStart"
    @drop="handleDrop"
  >
    <VCardTitle class="text-subtitle-1">
      <div class="d-flex align-center mb-2">
        <div class="d-flex align-start">
          <VTooltip bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VIcon
                v-bind="attrs"
                class="visibility-icon"
                :color="visibilityOptions[isEditing ? tmpView.visibility : view.visibility]?.color"
                data-test="visibility-icon"
                small
                v-on="on"
              >
                {{ visibilityOptions[isEditing ? tmpView.visibility : view.visibility]?.icon }}
              </VIcon>
            </template>
            <span>{{ tooltipText(isEditing ? tmpView.visibility : view.visibility) }}</span>
          </VTooltip>
          <input
            ref="rTitle"
            :class="[
              'title-input flex-grow-1 ml-2',
              { 'error--text': isEditing && isTitleOverLimit },
            ]"
            data-test="title-input"
            placeholder="Enter view title"
            :readonly="!isEditing"
            :value="isEditing ? tmpView.title : view.title"
            @focus="handleTitleFocus"
            @input="handleTitleInput"
            @keydown="handleKeyDown"
          />
        </div>
      </div>
    </VCardTitle>
    <VCardText class="mt-n4">
      <!-- when viewing the card, only show the description if it exists (to space vertical space) -->
      <textarea
        v-if="isEditing || view.description"
        ref="rDescription"
        :class="['description-input', { 'error--text': isEditing && isDescriptionOverLimit }]"
        data-test="description-input"
        :placeholder="isEditing ? 'Add a description for this view' : undefined"
        :readonly="!isEditing"
        rows="1"
        :value="isEditing ? tmpView.description : view.description"
        @focus="handleDescriptionFocus"
        @input="handleDescriptionInput"
        @keydown="handleKeyDown"
      />
    </VCardText>
    <!-- when viewing the card and there is no description, use a bigger negative margin to maximize vertical space -->
    <VCardActions
      :class="[
        'd-flex',
        'justify-space-between',
        !isEditing && !view.description ? 'mt-n6' : 'mt-n4',
      ]"
    >
      <template v-if="isEditing">
        <div class="visibility-selector mr-2" data-test="visibility-selector">
          <VBtnToggle v-model="tmpView.visibility" dense mandatory>
            <VTooltip v-for="[value, opt] in Object.entries(visibilityOptions)" :key="value" bottom>
              <template #activator="{ on, attrs }">
                <VBtn
                  v-bind="attrs"
                  :disabled="savedViewsStore.isSaving || !view.isOwner"
                  icon
                  small
                  :value="Number(value)"
                  v-on="on"
                >
                  <VIcon :color="opt.color" small>{{ opt.icon }}</VIcon>
                </VBtn>
              </template>
              <span>{{ tooltipText(Number(value)) }}</span>
            </VTooltip>
          </VBtnToggle>
        </div>
        <div class="d-flex align-center">
          <div class="character-counter mr-3" data-test="character-counter">
            <span
              :class="['text-caption', isCharCountOverLimit ? 'error--text' : 'text--secondary']"
            >
              {{ charCount }}
            </span>
          </div>
          <VTooltip bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="icon-action"
                data-test="cancel-edit-btn"
                :disabled="savedViewsStore.isSaving"
                icon
                small
                v-on="on"
                @click.stop="savedViewsStore.cancelEdit"
              >
                <VIcon size="18">mdi-close</VIcon>
              </VBtn>
            </template>
            <span>Cancel edit</span>
          </VTooltip>
          <VTooltip bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                color="primary"
                data-test="save-changes-btn"
                :disabled="!canSave || savedViewsStore.isSaving"
                icon
                :loading="savedViewsStore.isSaving"
                small
                v-on="on"
                @click.stop="handleSaveClick"
              >
                <VIcon size="18">mdi-check</VIcon>
              </VBtn>
            </template>
            <span>Save changes</span>
          </VTooltip>
        </div>
      </template>
      <template v-else>
        <div class="d-flex"></div>
        <div>
          <VTooltip bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="icon-action"
                data-test="duplicate-view-btn"
                icon
                small
                v-on="on"
                @click.stop="savedViewsStore.duplicateView(view)"
              >
                <VIcon size="18">mdi-content-copy</VIcon>
              </VBtn>
            </template>
            <span>Duplicate view</span>
          </VTooltip>
          <VTooltip bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="icon-action"
                data-test="delete-view-btn"
                :disabled="!view.isOwner"
                icon
                small
                v-on="on"
                @click.stop="savedViewsStore.deleteView(view)"
              >
                <VIcon size="18">mdi-delete</VIcon>
              </VBtn>
            </template>
            <span>Delete view</span>
          </VTooltip>
          <VTooltip
            v-if="
              view.isOwner || (!view.isOwner && view.visibility === UIConfigVisibility.READWRITE)
            "
            bottom
          >
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="icon-action"
                data-test="edit-view-btn"
                icon
                small
                v-on="on"
                @click.stop.prevent="handleEditClick"
              >
                <VIcon size="18">mdi-pencil</VIcon>
              </VBtn>
            </template>
            <span>Edit view</span>
          </VTooltip>
          <VTooltip
            v-if="
              view.isOwner || (!view.isOwner && view.visibility === UIConfigVisibility.READWRITE)
            "
            bottom
            open-delay="300"
          >
            <template #activator="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                class="icon-action"
                data-test="sync-view-btn"
                icon
                small
                v-on="on"
                @click.stop="savedViewsStore.syncView(view)"
              >
                <VIcon size="18">mdi-sync</VIcon>
              </VBtn>
            </template>
            <span>Sync view</span>
          </VTooltip>
        </div>
      </template>
    </VCardActions>
  </VCard>
</template>

<style lang="scss" scoped>
.v-card {
  position: relative;
  cursor: default;

  &:hover {
    border: 1px solid #555;
    background-color: #0a0a0a;
    cursor: default;
  }

  &.is-dragging {
    opacity: 0.5;
  }

  &.drag-over {
    cursor: move;
  }

  &.selected {
    border: 1px solid #4caf50;
  }

  &.dirty-view.selected {
    border: 1px solid orange;
  }
}

.v-card--link:before {
  background-color: transparent !important;
}

.title-input {
  border: none;
  outline: none;
  color: inherit;
  width: 15rem;
}

.description-input {
  width: 100%;
  border: none;
  outline: none;
  font-size: inherit;
  font-family: inherit;
  color: inherit;
  resize: none;
}

.visibility-icon {
  margin-top: 6px;
}

.visibility-selector .v-btn {
  margin: 0 2px;
  height: 24px;
  width: 24px;
}

.visibility-selector .v-btn--active {
  background-color: rgba(255, 255, 255, 0.12);
}

.icon-action {
  opacity: 0.7;
  &:hover {
    opacity: 1;
  }
}

.character-counter {
  display: flex;
  align-items: center;
  margin: 0 8px;

  span {
    font-size: 12px;
    font-weight: 500;
    opacity: 0.8;
  }
}
</style>
